import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, MessagesSquare, Mic, FileSignature, LogOut, Plus, ChevronRight } from 'lucide-react';
import { portalAPI, clientSession } from '@/lib/clientsAPI';
import { safeFormat } from '@/lib/utils';

const C = { ink: '#1E2A33', red: '#A62B1F', cream: '#F3EFE7', paper: '#FBF8F2', line: '#E3DDD1', muted: '#5C6670' };
const display = { fontFamily: 'Fraunces, Georgia, serif' };
const field = 'w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/20';

const Shell = ({ children, right }) => (
  <div className="min-h-screen" style={{ background: C.paper, color: C.ink, fontFamily: 'Manrope, Arial, sans-serif' }}>
    <header className="border-b bg-white" style={{ borderColor: C.line }}>
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
        <img src="/stagehead-os-mark.svg" alt="" className="w-8 h-8" />
        <div className="font-semibold" style={display}>Stagehead <span className="text-xs font-normal" style={{ color: C.muted }}>client portal</span></div>
        <div className="ml-auto">{right}</div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">{children}</main>
  </div>
);

const Card = ({ children, className = '' }) => <div className={`rounded-xl border bg-white p-5 md:p-6 ${className}`} style={{ borderColor: C.line }}>{children}</div>;
const Primary = ({ children, ...p }) => <button {...p} className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: C.red }}>{children}</button>;

// ── Sign in ──────────────────────────────────────────────────────────────────
export function ClientLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login'); // login | forgot | sent
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      if (mode === 'forgot') { await portalAPI.resetRequest(email); setMode('sent'); }
      else { const r = await portalAPI.login(email, password); clientSession.set(r.data.token); navigate('/client'); }
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Could not sign in.'); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <Card className="max-w-sm mx-auto">
        <h1 className="text-2xl font-semibold mb-1" style={display}>{mode === 'login' ? 'Sign in' : 'Reset your password'}</h1>
        <p className="text-sm mb-5" style={{ color: C.muted }}>{mode === 'login' ? 'Everything you\'re working on with your team, in one place.' : mode === 'sent' ? 'If that email has portal access, a reset link is on its way.' : 'We\'ll email you a link to choose a new password.'}</p>
        {mode !== 'sent' && (
          <form onSubmit={submit} className="space-y-3">
            <input className={field} style={{ borderColor: C.line }} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            {mode === 'login' && <input className={field} style={{ borderColor: C.line }} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />}
            {err && <p className="text-sm" style={{ color: C.red }}>{err}</p>}
            <Primary type="submit" disabled={busy}>{busy ? '…' : mode === 'login' ? 'Sign in' : 'Send reset link'}</Primary>
          </form>
        )}
        <button className="text-xs font-semibold mt-4" style={{ color: C.red }} onClick={() => { setErr(null); setMode(mode === 'login' ? 'forgot' : 'login'); }}>
          {mode === 'login' ? 'Forgot your password?' : 'Back to sign in'}
        </button>
      </Card>
    </Shell>
  );
}

// ── Accept invite ────────────────────────────────────────────────────────────
export function ClientInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [bad, setBad] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { portalAPI.inviteInfo(token).then((r) => { setInfo(r.data); setName(r.data.name || ''); }).catch(() => setBad(true)); }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await portalAPI.acceptInvite(token, { name, password }); clientSession.set(r.data.token); navigate('/client'); }
    catch (ex) { setErr(ex?.response?.data?.detail || 'Could not continue.'); } finally { setBusy(false); }
  };
  if (bad) return <Shell><Card className="max-w-sm mx-auto">This invite link isn't valid any more. <Link to="/client/login" className="font-semibold" style={{ color: C.red }}>Sign in</Link> if you already have access.</Card></Shell>;
  if (!info) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div></Shell>;
  return (
    <Shell>
      <Card className="max-w-sm mx-auto">
        <h1 className="text-2xl font-semibold mb-1" style={display}>Your {info.company} portal</h1>
        <p className="text-sm mb-5" style={{ color: C.muted }}>{info.has_password ? `Sign in as ${info.email} to add ${info.company} to your portal.` : `Set a password for ${info.email} and you're in.`}</p>
        <form onSubmit={submit} className="space-y-3">
          {!info.has_password && <input className={field} style={{ borderColor: C.line }} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />}
          <input className={field} style={{ borderColor: C.line }} type="password" placeholder={info.has_password ? 'Password' : 'Choose a password (8+ characters)'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={info.has_password ? 'current-password' : 'new-password'} />
          {err && <p className="text-sm" style={{ color: C.red }}>{err}</p>}
          <Primary type="submit" disabled={busy}>{busy ? '…' : 'Open my portal'}</Primary>
        </form>
      </Card>
    </Shell>
  );
}

// ── Reset password ───────────────────────────────────────────────────────────
export function ClientReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await portalAPI.reset(token, password); clientSession.set(r.data.token); navigate('/client'); }
    catch (ex) { setErr(ex?.response?.data?.detail || 'Could not reset.'); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <Card className="max-w-sm mx-auto">
        <h1 className="text-2xl font-semibold mb-4" style={display}>Choose a new password</h1>
        <form onSubmit={submit} className="space-y-3">
          <input className={field} style={{ borderColor: C.line }} type="password" placeholder="New password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          {err && <p className="text-sm" style={{ color: C.red }}>{err}</p>}
          <Primary type="submit" disabled={busy}>Save and sign in</Primary>
        </form>
      </Card>
    </Shell>
  );
}

// ── Portal home ──────────────────────────────────────────────────────────────
const Row = ({ to, icon: Icon, title, sub, badge, dot }) => (
  <a href={to} className="flex items-center gap-3 py-3 px-1 hover:bg-[#FBF8F2] rounded-lg">
    <Icon className="w-4 h-4 shrink-0" style={{ color: C.red }} />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold truncate flex items-center gap-2">{dot && <span className="w-2 h-2 rounded-full" style={{ background: C.red }} />}{title}</div>
      {sub && <div className="text-xs truncate" style={{ color: C.muted }}>{sub}</div>}
    </div>
    {badge && <span className="text-xs rounded-full px-2 py-0.5 border shrink-0" style={{ borderColor: C.line, background: C.cream }}>{badge}</span>}
    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.muted }} />
  </a>
);

export default function Portal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [compose, setCompose] = useState(null); // company id
  const [msg, setMsg] = useState({ title: '', text: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => portalAPI.overview().then((r) => setData(r.data)).catch((e) => {
    if (e?.response?.status === 401) { clientSession.clear(); navigate('/client/login'); }
  }), [navigate]);
  useEffect(() => { if (!clientSession.get()) navigate('/client/login'); else load(); }, [load, navigate]);

  const signOut = () => { clientSession.clear(); navigate('/client/login'); };
  const start = async () => {
    setBusy(true);
    try { const r = await portalAPI.startYarn({ company_id: compose, ...msg }); window.location.href = r.data.url; }
    catch (e) { alert(e?.response?.data?.detail || 'Could not send'); setBusy(false); }
  };

  if (!data) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div></Shell>;
  return (
    <Shell right={<button onClick={signOut} className="inline-flex items-center gap-1 text-sm" style={{ color: C.muted }}><LogOut className="w-4 h-4" />Sign out</button>}>
      <h1 className="text-2xl md:text-3xl font-semibold mb-1" style={display}>Hi {(data.name || '').split(' ')[0] || 'there'}</h1>
      <p className="text-sm mb-6" style={{ color: C.muted }}>Everything you're working on, in one place.</p>
      {data.companies.length === 0 && <Card>Your access isn't active right now. Contact the team that invited you.</Card>}
      <div className="space-y-6">
        {data.companies.map((co) => {
          const empty = !co.yarns.length && !co.interviews.length && !co.signatures.length;
          return (
            <Card key={co.company.id}>
              <div className="flex items-center gap-3 mb-3">
                {co.company.logo_url && <img src={co.company.logo_url} alt="" className="h-8 max-w-[120px] object-contain" />}
                <h2 className="text-lg font-semibold" style={display}>{co.company.name}</h2>
                <button onClick={() => { setCompose(co.company.id); setMsg({ title: '', text: '' }); }} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.red }}><Plus className="w-4 h-4" />Message the team</button>
              </div>
              {compose === co.company.id && (
                <div className="rounded-lg p-3 mb-3 space-y-2" style={{ background: C.cream }}>
                  <input className={field} style={{ borderColor: C.line }} placeholder="Subject" value={msg.title} onChange={(e) => setMsg({ ...msg, title: e.target.value })} />
                  <textarea className={field} style={{ borderColor: C.line }} rows={3} placeholder="Your message" value={msg.text} onChange={(e) => setMsg({ ...msg, text: e.target.value })} />
                  <div className="flex gap-2"><button onClick={() => setCompose(null)} className="text-sm px-3" style={{ color: C.muted }}>Cancel</button>
                    <button onClick={start} disabled={busy || !msg.title.trim() || !msg.text.trim()} className="ml-auto rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: C.red }}>Send</button></div>
                </div>
              )}
              {empty && <p className="text-sm" style={{ color: C.muted }}>Nothing shared with you yet. When the team starts a conversation or sends you something, it'll appear here.</p>}
              {co.signatures.length > 0 && <><h3 className="text-xs font-bold uppercase tracking-widest mt-2" style={{ color: C.muted }}>To sign</h3>
                {co.signatures.map((s, i) => <Row key={i} to={s.url} icon={FileSignature} title={s.title} sub={safeFormat(s.created_at, 'MMM d, yyyy')} badge={s.status} dot={s.status === 'Waiting for you'} />)}</>}
              {co.interviews.length > 0 && <><h3 className="text-xs font-bold uppercase tracking-widest mt-3" style={{ color: C.muted }}>Interview and press kit</h3>
                {co.interviews.map((iv, i) => <Row key={i} to={iv.url} icon={Mic} title={iv.title} badge={iv.stage} dot={iv.stage === 'Ready for you'} />)}</>}
              {co.yarns.length > 0 && <><h3 className="text-xs font-bold uppercase tracking-widest mt-3" style={{ color: C.muted }}>Conversations</h3>
                {co.yarns.map((y, i) => <Row key={i} to={y.url} icon={MessagesSquare} title={y.title} sub={y.preview} badge={y.archived ? 'Closed' : safeFormat(y.last_post_at, 'MMM d')} dot={y.unread} />)}</>}
            </Card>
          );
        })}
      </div>
    </Shell>
  );
}
